import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoDTO } from '@src/application/pascos/dto';

/**
 * Hook: useScheduleReviewPasco
 * Marks a PASCO record for review
 *
 * @param options - React Query mutation options
 * @returns Mutation result for scheduling PASCO review
 *
 * @example
 * ```typescript
 * const { mutate: scheduleReviewPasco, isPending, error } = useScheduleReviewPasco({
 *     onSuccess: (pasco) => {
 *         console.log('PASCO scheduled for review:', pasco);
 *     }
 * });
 *
 * // Usage
 * scheduleReviewPasco('pasco-123');
 * ```
 */
export function useScheduleReviewPasco(
    options?: Omit<UseMutationOptions<PascoDTO, Error, string>, 'mutationFn'>
) {
    const pascoService = getPascoService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (pascoId: string) => {
            return await pascoService.scheduleReview(pascoId);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch pascos list and pending review queries
            await queryClient.invalidateQueries({
                queryKey: ['pascos'],
                predicate: (query) =>
                    query.queryKey.length === 1 || // General pascos list
                    query.queryKey[1] === 'pending-review' // Pending review queries
            });

            // Update the specific pasco in cache
            queryClient.setQueryData(['pascos', variables], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        }
    });
}